//LabFilingDetails
import React from "react";
import { Box, Paper, Typography, Grid } from "@mui/material";
import NodeViewer from "./NodeViewer";
import LabReportGrid from "./LabReportGrid";
import NodesArrayViewer from "./NodesArrayViewer";
import { useSelector } from "react-redux";
import { sectionPaperSx, sectionHeadingSx, innerCardSx } from "./sectionTheme";

const columnHeadingSx = {
  color: "primary.main",
  fontWeight: "bold",
  mb: 1,
  borderBottom: "2px solid",
  borderColor: "primary.main",
  display: "inline-block",
  pb: 0.5,
};

const LabFilingDetails = ({ lab }) => {
  const access_token = useSelector((state) => state.authen.accessToken);
  const is_admin = Boolean(access_token);

  if (!lab) return null;

  const filingErrors = Array.isArray(lab.filing_error)
    ? lab.filing_error
    : lab.filing_error
      ? [lab.filing_error]
      : [];

  return (
    <>
      <Paper elevation={3} sx={sectionPaperSx("patient")}>
        <Typography variant="h5" sx={sectionHeadingSx("patient")}>
          1. Patient Background
        </Typography>

        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "stretch",
          }}
        >
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              pr: 2,
              borderRight: "2px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={columnHeadingSx}>
              Patient Data
            </Typography>

            <Typography variant="body2">
              <strong>EMIS ID:</strong> {lab.patient_data?.emis_id}
            </Typography>
            <Typography variant="body2">
              <strong>NHS Number:</strong> {lab.patient_data?.nhs_num}
            </Typography>
            <Typography variant="body2">
              <strong>Received Date:</strong> {lab.patient_data?.received_date}
            </Typography>
            <Typography variant="body2">
              <strong>Issue Date:</strong> {lab.patient_data?.issue_date}
            </Typography>
            <Typography variant="body2">
              <strong>Gender:</strong> {lab.patient_data?.gender}
            </Typography>
            <Typography variant="body2">
              <strong>Age:</strong> {lab.patient_data?.age}
            </Typography>
            <Typography variant="body2">
              <strong>Report ID:</strong> {lab.patient_data?.lab_report_id}
            </Typography>
            <Typography variant="body2">
              <strong>Report Type:</strong> {lab.patient_data?.report_type}
            </Typography>
            <Typography variant="body2">
              <strong>Usual GP:</strong> {lab.patient_data?.usual_gp}
            </Typography>
            <Typography variant="body2">
              <strong>Ordering Party:</strong>{" "}
              {lab.patient_data?.ordering_party}
            </Typography>

            <Typography variant="body2">
              <strong>Approval Status:</strong>{" "}
              <Box
                component="span"
                sx={{
                  color: lab.approved ? "#388e3c" : "#f57c00",
                  fontWeight: "bold",
                }}
              >
                {lab.approved ? "Approved" : "Not Approved"}
              </Box>
            </Typography>
          </Grid>

          {/* Column 2: Patient Profile Summary */}
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              px: 2,
              borderRight: "2px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={columnHeadingSx}>
              Patient Profile Summary
            </Typography>

            <Box sx={{ mt: 1 }}>
              {lab.patient_profile?.health_status &&
                Object.entries(lab.patient_profile.health_status).map(
                  ([key, val]) => (
                    <Typography key={key} variant="body2">
                      <strong>{key}:</strong> {val?.value || "-"}{" "}
                      {val?.date ? `(${val.date})` : ""}
                    </Typography>
                  ),
                )}
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={3}
            sx={{
              px: 2,
              borderRight: "2px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={columnHeadingSx}>
              Medications
            </Typography>

            <Box sx={{ mt: 1, maxWidth: 300 }}>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                <strong>Acute:</strong>{" "}
                {lab.patient_profile?.medications?.acute?.length
                  ? lab.patient_profile.medications.acute.join(", ")
                  : "None"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                <strong>Repeat:</strong>{" "}
                {lab.patient_profile?.medications?.repeat?.length
                  ? lab.patient_profile.medications.repeat.join(", ")
                  : "None"}
              </Typography>
            </Box>
          </Grid>

          {/* Column 4: Problems */}
          <Grid item xs={12} md={3} sx={{ pl: 2 }}>
            <Typography variant="h6" sx={columnHeadingSx}>
              Problems
            </Typography>

            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                Active
              </Typography>
              {lab.patient_profile?.problems?.active &&
              Object.keys(lab.patient_profile.problems.active).length > 0 ? (
                Object.entries(lab.patient_profile.problems.active).map(
                  ([problem, date]) => (
                    <Typography key={problem} variant="body2">
                      {problem} {date ? `(${date})` : ""}
                    </Typography>
                  ),
                )
              ) : (
                <Typography variant="body2">None</Typography>
              )}

              <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                Past
              </Typography>
              {lab.patient_profile?.problems?.past?.length ? (
                lab.patient_profile.problems.past.map((p, i) => (
                  <Typography key={i} variant="body2">
                    {p}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2">None</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <LabReportGrid report={lab.patient_data?.report_data} />

      <Paper elevation={3} sx={sectionPaperSx("evaluation")}>
        <Typography variant="h5" sx={sectionHeadingSx("evaluation")}>
          4. Result Evaluation Summary
        </Typography>

        {Array.isArray(lab.categories) && lab.categories.length > 0 ? (
          lab.categories.map((cat, idx) => (
            <Paper
              key={cat.current_ruleset?.id || idx}
              elevation={0}
              sx={innerCardSx("evaluation")}
            >
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {cat.category_name}
              </Typography>

              {cat.current_ruleset ? (
                <Box sx={{ pl: 2 }}>
                  <NodeViewer
                    root={cat.current_ruleset}
                    selectedCategory={cat.category_name}
                    nodeConfigs={{}}
                    onSelectNode={() => {}}
                    selectedNodeId={null}
                    showTooltip={false}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No ruleset available.
                </Typography>
              )}
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No categories found in this report.
          </Typography>
        )}
      </Paper>

      <Paper elevation={3} sx={sectionPaperSx("summary")}>
        <Typography variant="h5" sx={sectionHeadingSx("summary")}>
          5. Summarized MyBotGP Actions
        </Typography>

        {is_admin && filingErrors.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#fff8f8",
              border: "1px solid #f3c8c8",
              borderLeft: "5px solid",
              borderLeftColor: "error.main",
              borderRadius: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "error.main",
                fontWeight: "bold",
                mb: 1,
              }}
            >
              Filing Errors
            </Typography>

            {filingErrors.map((error, idx) => (
              <Box
                key={idx}
                component="pre"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowX: "auto",
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                  m: 0,
                  mb: idx < filingErrors.length - 1 ? 2 : 0,
                }}
              >
                {typeof error === "object"
                  ? JSON.stringify(error, null, 2)
                  : error}
              </Box>
            ))}
          </Paper>
        )}

        <NodesArrayViewer
          nodes={lab.actions}
          nodeConfigs={{}}
          onSelectNode={() => {}}
          selectedNodeId={null}
        />
      </Paper>
    </>
  );
};

export default LabFilingDetails;
